import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { ListItemButton, ListItemIcon } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import StoreIcon from '@mui/icons-material/Store';

import { useOffices } from '../../../hooks/useOffices';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import OfficeForm from '../forms/OfficeForm';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useDeleteOffice } from '../../../hooks/useDeleteOffice';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';

export default function Offices() {
    const { t } = useTranslation('common');
    const [open, setOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [selectedOffice, setSelectedOffice] = React.useState(null);
    const [formMode, setFormMode] = React.useState("create");

    const { offices, loading, setOffices } = useOffices();
    const { remove } = useDeleteOffice(setOffices);
    const { showSnackbar } = useSnackbar();

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleAdd = () => {
        setSelectedOffice(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (office) => {
        setSelectedOffice(office);
        setFormMode("edit");
        setFormOpen(true);
    };

    const handleDelete = async (office) => {
        try {
        await remove(office.id);
        showSnackbar(t('offices.messages.delete_success', { name: office.name }), "success");
        } catch (err) {
        showSnackbar(t('offices.messages.delete_fail', { name: office.name }), "error");
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    // Group offices by company
    const groupedCompanies = offices.reduce((acc, office) => {
        const companyId = office.company?.id;
        if (!acc[companyId]) {
        acc[companyId] = {
            ...office.company,
            offices: []
        };
        }
        acc[companyId].offices.push(office);
        return acc;
    }, {});
    const companies = Object.values(groupedCompanies).map(company => ({
        ...company,
        offices: company.offices.sort((a, b) => a.name.localeCompare(b.name))
    })).sort((a, b) => a.name.localeCompare(b.name));



    return (
        <React.Fragment>
            <ListItem>
                <ListItemButton onClick={handleClickOpen} >
                    <ListItemIcon>
                        <StoreIcon fontSize='large' />
                    </ListItemIcon>

                    <ListItemText 
                        primary="Company Offices"
                        secondary="view, add, edit, delete offices"
                    >
                    </ListItemText>
                </ListItemButton>
            </ListItem>

            <Dialog onClose={handleClose} open={open} disableRestoreFocus>
                <DialogTitle>Company Offices</DialogTitle>

                <DialogContent dividers>
                <List>
                    {companies.map((company) => (
                    <React.Fragment key={company.id}>
                        <ListSubheader>{company.name}</ListSubheader>
                        {company.offices.map((office) => (
                        <ListItem key={office.id} sx={{ pl: 4, py: 0}}>
                            <ListItemText primary={office.name} />
                            <Box sx={{ ml: 'auto' }}>
                            <VerticalHamburgerMenu
                                options={[
                                { id: 1, name: 'Edit', callback: () => handleEdit(office) },
                                { id: 2, name: 'Delete', callback: () => handleDelete(office),
                                    confirmMessage: t('offices.messages.delete_confirm', { name: office.name }) },
                                ]}
                            />
                            </Box>
                        </ListItem>
                        ))}
                    </React.Fragment>
                    ))}
                </List>

                <OfficeForm
                    setOffices={setOffices}
                    office={selectedOffice}
                    mode={formMode}
                    open={formOpen}
                    onClose={() => setFormOpen(false)}
                />
                </DialogContent>

                <DialogActions>
                <Button onClick={handleAdd}>Add Office</Button>
                <div style={{ flex: '1 0 0' }} />
                <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}